import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Spinner,
  Center,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Icon,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Tooltip,
  Flex,
  Divider,
  Tag,
  TagLabel,
  TagLeftIcon,
  IconButton,
} from '@chakra-ui/react';
import {
  FaEye,
  FaFileAlt,
  FaCalendarAlt,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaSync,
} from 'react-icons/fa';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import { Cross, CrossIcon } from 'lucide-react';
import { GiCrossMark } from 'react-icons/gi';

const UploadStatusModal = ({ isOpen, onClose, schemeId, financialYear }) => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);
  const [totalStats, setTotalStats] = useState({ totalYes: 0, totalNo: 0 });
  const toast = useToast();
  const token = localStorage.getItem('token');

  // All useColorModeValue hooks MUST be at the top level, before any conditionals
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('white', 'gray.800');
  const modalBg = useColorModeValue('white', 'gray.800');
  const tableHeaderBg = useColorModeValue('green.200', 'green.700');
  const evenRowBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');

  // Status badge colors
  const yesBadgeBg = useColorModeValue('green.100', 'green.900');
  const yesBadgeColor = useColorModeValue('green.800', 'green.100');
  const noBadgeBg = useColorModeValue('red.100', 'red.900');
  const noBadgeColor = useColorModeValue('red.800', 'red.100');

  // Background colors for summary
  const summaryBg = useColorModeValue('gray.50', 'gray.700');

  const fetchUploadStatus = useCallback(async () => {
    if (!schemeId || !financialYear) return;

    setLoading(true);
    setError(null);
    try {
      const url = `${baseUrl}/upload-file/upload-status/scheme/${schemeId}?month&currentFY=${financialYear}`;

      const response = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setStatusData(response.data);

      // Calculate total counts
      if (response.data?.data) {
        calculateTotalStats(response.data.data);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message ||
        error?.response?.data?.data ||
        'Failed to fetch upload status';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [schemeId, financialYear, token, toast]);

  // Calculate total Yes and No counts across all files
  const calculateTotalStats = (data) => {
    let totalYes = 0;
    let totalNo = 0;

    data.forEach(scenario => {
      scenario.data.forEach(row => {
        Object.entries(row).forEach(([key, value]) => {
          if (key !== 'fileTypes names' && key !== 'Month' && key !== 'Scenario') {
            const val = String(value).toLowerCase();
            if (val === 'yes') totalYes++;
            else if (val === 'no') totalNo++;
          }
        });
      });
    });

    setTotalStats({ totalYes, totalNo });
  };

  useEffect(() => {
    if (isOpen && schemeId && financialYear) {
      fetchUploadStatus();
    }
  }, [isOpen, schemeId, financialYear, fetchUploadStatus]);

  // Get status display for cell value
  const getStatusDisplay = (value) => {
    const val = String(value).toLowerCase();

    if (val === 'yes') {
      return (
        <Tooltip label="Uploaded" hasArrow placement="top">
          <Tag size="sm" variant="subtle" colorScheme="green" borderRadius="full">
            <TagLeftIcon as={FaCheckCircle} boxSize="12px" />
            <TagLabel>Yes</TagLabel>
          </Tag>
        </Tooltip>
      );
    }
    if (val === 'no') {
      return (
        <Tooltip label="Not Uploaded" hasArrow placement="top">
          <Tag size="sm" variant="subtle" colorScheme="red" borderRadius="full">
            <TagLeftIcon as={FaTimesCircle} boxSize="12px" />
            <TagLabel>No</TagLabel>
          </Tag>
        </Tooltip>
      );
    }
    if (val === '-') {
      return <Text color={textColor}>-</Text>;
    }

    return <Text>{value || '-'}</Text>;
  };

  // Export to Excel using API
  const exportToExcel = async () => {
    if (!schemeId || !financialYear) {
      toast({
        title: 'Missing Information',
        description: 'Scheme ID or Financial Year is missing',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setExportLoading(true);
    try {
      const url = `${baseUrl}/upload-file/upload-status/scheme/${schemeId}/export`;

      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        params: {
          month: '',
          currentFY: financialYear
        },
        responseType: 'blob'
      });

      if (response.data instanceof Blob) {
        if (response.data.type === 'application/json') {
          const errorText = await response.data.text();
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || errorJson.error || 'Failed to export data');
          } catch {
            throw new Error('Failed to export data');
          }
        }

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        const contentDisposition = response.headers['content-disposition'];
        let fileName = `upload_status_${schemeId}_${new Date().toISOString().split('T')[0]}.xlsx`;

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, '');
          }
        }

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast({
          title: 'Export Successful',
          description: 'Excel file downloaded successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Export error:', error);

      let errorMessage = 'Failed to export data to Excel';

      if (error.response) {
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            errorMessage = `Server error: ${error.response.status}`;
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || errorMessage;
      }

      toast({
        title: 'Export Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setExportLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchUploadStatus();
    toast({
      title: 'Refreshing',
      description: 'Fetching latest data...',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  if (!isOpen) return null;

  function getCurrentMonthName() {
    const date = new Date();
    return date.toLocaleString('default', { month: 'long' });
  }

  let currentMonthName = getCurrentMonthName();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="8xl"
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        mx={4}
        my={4}
        maxH="98vh"
        bg={modalBg}
        boxShadow="2xl"
      >
        <ModalHeader
          bg={headerBg}
          borderTopRadius="2xl"
          py={2}
          borderBottom="1px"
          borderBottomColor={borderColor}
        >
          <VStack spacing={4} align="stretch">
            <HStack spacing={4} justify="space-between">
              <HStack spacing={4}>
                <Flex
                  w={12}
                  h={10}
                  borderRadius="full"
                  bg={`${accentColor}20`}
                  align="center"
                  justify="center"
                >
                  <Icon as={FaEye} boxSize={6} color={accentColor} />
                </Flex>
                <Box>
                  <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                    MIS Report : {statusData?.data?.[0]?.schemeName || 'Scheme'}
                  </Text>
                  <HStack spacing={3} mt={1}>
                    <HStack spacing={1}>
                      <Icon as={FaCalendarAlt} color={accentColor} boxSize={4} />
                      <Text fontSize="sm" color={textColor} fontWeight="medium">
                        FY: {financialYear ? new Date(financialYear).getFullYear() : 'N/A'}
                      </Text>
                    </HStack>
                    <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                      {statusData?.data?.[0]?.schemeName || 'Scheme'}
                    </Badge>
                    <Badge colorScheme="pink" variant="subtle" px={3} py={1} borderRadius="full">
                      {currentMonthName}
                    </Badge>
                  </HStack>
                </Box>
              </HStack>

              <HStack spacing={3}>
                {!loading && !error && statusData?.data && (
                  <Flex
                    justify="space-between"
                    align="center"
                    bg={summaryBg}
                    p={3}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    mt={2}
                  >
                    <HStack spacing={6}>
                      <Flex align="center" bg={yesBadgeBg} px={4} py={2} borderRadius="full">
                        <Icon as={FaCheckCircle} color="green.500" boxSize={5} />
                        <Text fontSize="md" fontWeight="bold" color={yesBadgeColor} ml={2}>
                          Data Uploaded: {totalStats.totalYes}
                        </Text>
                      </Flex>
                      <Flex align="center" bg={noBadgeBg} px={4} py={2} borderRadius="full">
                        <Icon as={FaTimesCircle} color="red.500" boxSize={5} />
                        <Text fontSize="md" fontWeight="bold" color={noBadgeColor} ml={2}>
                          NOT Uploaded: {totalStats.totalNo}
                        </Text>
                      </Flex>
                    </HStack>
                  </Flex>
                )}
                <Tooltip label="Refresh Data" hasArrow>
                  <IconButton
                    icon={<FaSync />}
                    onClick={handleRefresh}
                    isLoading={loading}
                    aria-label="Refresh"
                    variant="ghost"
                    colorScheme="blue"
                    size="lg"
                    isRound
                  />
                </Tooltip>

                <Tooltip label="Export to Excel" hasArrow>
                  <Button
                    colorScheme="blue"
                    leftIcon={<FaFileExcel />}
                    onClick={exportToExcel}
                    isLoading={exportLoading}
                    loadingText="Exporting..."
                    size="lg"
                    isDisabled={!statusData?.data || statusData.data.length === 0}
                    borderRadius="full"
                    px={6}
                    boxShadow="md"
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                    transition="all 0.2s"
                  >
                    Export In Excel
                  </Button>
                </Tooltip>
              </HStack>
            </HStack>
          </VStack>
        </ModalHeader>

        {/* <ModalCloseButton mt={5} mr={5} /> */}

        <ModalBody py={6} px={5}>
          {loading ? (
            <Center py={20}>
              <VStack spacing={6}>
                <Spinner
                  size="xl"
                  thickness="4px"
                  speed="0.65s"
                  color="blue.500"
                  emptyColor="gray.200"
                />
                <Text color={textColor} fontSize="lg" fontWeight="medium">
                  Fetching upload status...
                </Text>
              </VStack>
            </Center>
          ) : error ? (
            <Alert
              status="error"
              borderRadius="xl"
              flexDirection="column"
              alignItems="center"
              py={10}
              variant="subtle"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <Text fontSize="lg" fontWeight="medium" mt={4}>{error}</Text>
              <Button
                mt={6}
                colorScheme="red"
                onClick={fetchUploadStatus}
                leftIcon={<FaSync />}
                size="lg"
                borderRadius="full"
              >
                Retry
              </Button>
            </Alert>
          ) : !statusData?.data || statusData.data.length === 0 ? (
            <Center py={20}>
              <VStack spacing={6}>
                <Icon as={FaFileAlt} boxSize={16} color="gray.400" />
                <Text color={textColor} fontSize="lg" fontWeight="medium">
                  No upload status data available
                </Text>
                <Button
                  colorScheme="blue"
                  onClick={fetchUploadStatus}
                  leftIcon={<FaSync />}
                  size="md"
                  borderRadius="full"
                >
                  Refresh
                </Button>
              </VStack>
            </Center>
          ) : (
            <VStack spacing={6} align="stretch">
              {statusData.data.map((scenarioData, scenarioIndex) => (
                <Card
                  key={scenarioIndex}
                  variant="outline"
                  borderRadius="xl"
                  overflow="hidden"
                  bg={modalBg}
                  boxShadow="md"
                  _hover={{ boxShadow: 'lg' }}
                  transition="all 0.2s"
                >
                  <CardBody p={0}>
                    {/* Scrollable Table Container */}
                    <Box 
                      maxH="500px"
                      overflowY="auto"
                      overflowX="auto"
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                          height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: ('gray.100', 'gray.700'),
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: ('gray.400', 'gray.500'),
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: ('gray.500', 'gray.400'),
                        },
                      }}
                    >
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            {scenarioData.headers.map((header, idx) => (
                              <Th
                                key={idx}
                                color={textColor}
                                fontWeight="semibold"
                                fontSize="xs"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                borderRight={idx < scenarioData.headers.length - 1 ? '1px' : 'none'}
                                borderRightColor={borderColor}
                                whiteSpace="normal"
                                minW={idx === 0 ? "280px" : "100px"}
                                maxW={idx === 0 ? "350px" : "150px"}
                                bg={tableHeaderBg}
                                py={3}
                                position="sticky"
                                top={0}
                                zIndex={1}
                                boxShadow="sm"
                              >
                                {header}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {scenarioData.data.map((row, rowIndex) => (
                            <Tr
                              key={rowIndex}
                              bg={rowIndex % 2 === 0 ? evenRowBg : 'transparent'}
                              transition="background 0.2s"
                            >
                              {scenarioData.headers.map((header, colIndex) => {
                                const value = row[header];
                                return (
                                  <Td
                                    key={colIndex}
                                    borderRight={colIndex < scenarioData.headers.length - 1 ? '1px' : 'none'}
                                    borderRightColor={borderColor}
                                    fontSize="sm"
                                    py={2.5}
                                  >
                                    {getStatusDisplay(value)}
                                  </Td>
                                );
                              })}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                    
                    {/* Row count indicator */}
                    <Flex 
                      justify="flex-end" 
                      align="center" 
                      px={4} 
                      py={2} 
                      bg={summaryBg}
                      borderTop="1px"
                      borderColor={borderColor}
                    >
                      <Text fontSize="xs" color={textColor}>
                        Total Rows: {scenarioData.data.length}
                      </Text>
                    </Flex>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter
          borderTop="1px"
          borderColor={borderColor}
          bg={headerBg}
          borderBottomRadius="2xl"
          py={4}
          px={6}
        >
          <HStack spacing={4} width="100%" justify="space-between">
            <HStack spacing={6}>
              {/* <HStack spacing={3}>
                <Flex align="center" bg={yesBadgeBg} px={3} py={1.5} borderRadius="full">
                  <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                  <Text fontSize="sm" fontWeight="medium" color={yesBadgeColor} ml={2}>
                    Yes - Uploaded
                  </Text>
                </Flex>
                <Flex align="center" bg={noBadgeBg} px={3} py={1.5} borderRadius="full">
                  <Icon as={FaTimesCircle} color="red.500" boxSize={4} />
                  <Text fontSize="sm" fontWeight="medium" color={noBadgeColor} ml={2}>
                    No - Not Uploaded 
                  </Text>
                </Flex>
              </HStack> */}
              <Divider orientation="vertical" height="24px" />
             
            </HStack>
            <Button
              colorScheme="blue"
              onClick={onClose}
              px={8}
              variant="ghost"
              borderRadius="full"
              _hover={{ bg: `${accentColor}20` }}
            >
             <GiCrossMark boxSize={4} mr={2} />
             Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadStatusModal;